import { collection, getDocs } from "firebase/firestore";
import React, { useState, useEffect } from "react";
import { db } from "../../firebase/firebase";
import '../AllRetailers/AllRetailer.css';
import { getUserInventorySummary } from '../../hooks/userInventoryData';
import { useDistributor } from '../../hooks/useDistributor';

function ShowRetailers() {
    const [inventory, setRetailer] = useState([]);
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inventorySummaries, setInventorySummaries] = useState({});

    const user = useDistributor();

    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const inventoryCollection = collection(db, 'users');
                const inventorySnapshot = await getDocs(inventoryCollection);
                const inventoryList = inventorySnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                    }))
                    .filter(retailer => retailer.role.toLowerCase() === 'retailer' && retailer.createdBy?.id === user.uid);

                setRetailer(inventoryList);
                setFilteredInventory(inventoryList);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (user?.uid) {
            fetchInventory();
        }
    }, [user]);

    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);

        const filtered = inventory.filter((retailer) =>
            retailer.name?.toLowerCase().includes(value) ||
            retailer.userName?.toLowerCase().includes(value) ||
            retailer.email?.toLowerCase().includes(value) ||
            retailer.mobile?.toString().toLowerCase().includes(value) ||
            retailer.address?.toLowerCase().includes(value) ||
            retailer.city?.toLowerCase().includes(value) ||
            retailer.state?.toLowerCase().includes(value) ||
            (retailer.pinCode || retailer.pincode)?.toString().toLowerCase().includes(value)
        );

        setFilteredInventory(filtered);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setFilteredInventory(inventory);
    };

    const fetchSummary = async (uid) => {
        try {
            const result = await getUserInventorySummary(uid);
            return { totalInventory: result.totalInventory, distinctBrandsCount: result.distinctBrands.length };
        } catch (error) {
            console.error("Failed to fetch inventory summary:", error.message);
            return { totalInventory: 0, distinctBrandsCount: 0 };
        }
    };

    useEffect(() => {
        const fetchAllSummaries = async () => {
            const summaries = {};
            await Promise.all(
                filteredInventory.map(async (retailer) => {
                    const { totalInventory, distinctBrandsCount } = await fetchSummary(retailer.uid);
                    summaries[retailer.uid] = { totalInventory, distinctBrandsCount };
                })
            );
            setInventorySummaries(summaries);
        };

        if (filteredInventory.length > 0) {
            fetchAllSummaries();
        }
    }, [filteredInventory]);

    return (
        <div className="admin-layout">
            <div className="search-bar">
                <input
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearch}
                    className="search-input"
                />
                <div className="btns">
                    <button onClick={handleSearch} className="search-button">Search</button>
                    <button onClick={clearSearch} className="clear-button">Clear</button>
                </div>
            </div>

            {loading && <div>Loading...</div>}
            {error && <div className="error">Error: {error}</div>}

            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Username</th>
                        <th>Password</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Address</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Pincode</th>
                        <th>Total Items</th>
                        <th>Total Brands</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredInventory.map((retailer, index) => (
                        <tr key={index}>
                            <td>{retailer.name}</td>
                            <td>{retailer.userName}</td>
                            <td>{retailer.password}</td>
                            <td>{retailer.email}</td>
                            <td>{retailer.mobile}</td>
                            <td>{retailer.address || 'N/A'}</td>
                            <td>{retailer.city || 'N/A'}</td>
                            <td>{retailer.state || 'N/A'}</td>
                            <td>{retailer.pinCode || retailer.pincode || 'N/A'}</td>
                            <td>{inventorySummaries[retailer.uid]?.totalInventory ?? 'Loading...'}</td>
                            <td>{inventorySummaries[retailer.uid]?.distinctBrandsCount ?? 'Loading...'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {!loading && filteredInventory.length === 0 && (
                <div className="no-data">No retailers found.</div>
            )}
        </div>
    );
}

export default ShowRetailers;
